import { useCallback } from 'react'
import {
  validateSqmOnRange,
  getLaborTime,
  getPaintingMaterial,
} from '_/views/inspection-budget/utils/functions'
import { LABOR_TYPE_SLUGS } from '_/views/inspection-budget/utils/constants'

export function useServiceLaborUpdate({
  getValues,
  setValue,
  showToast,
  getAndUpdateLaborValue,
  updateExecutionDue,
}) {
  const ensureDefaultMaterial = useCallback(
    (fieldName, unitPrice, nextPaintingMaterial) => {
      const quantity = nextPaintingMaterial?.quantity ?? 1
      setValue(
        `${fieldName}.budgetResolutionMaterials`,
        [
          {
            description: 'Material',
            unid: nextPaintingMaterial?.unid ?? null,
            quantity,
            unitPrice,
            total: Number(quantity || 0) * Number(unitPrice || 0),
          },
        ],
        { shouldValidate: true, shouldDirty: true }
      )
    },
    [setValue]
  )

  const updateCurrentMaterial = useCallback(
    (current, fieldName, unitPrice, nextPaintingMaterial) => {
      const quantity = nextPaintingMaterial?.quantity ?? 1
      const total = Number(quantity || 0) * Number(unitPrice || 0)
      const currentMaterials = getValues(`${fieldName}.budgetResolutionMaterials`) ?? []
      const nextMaterials = [...currentMaterials]

      nextMaterials[0] = {
        ...current,
        description: current?.description ?? 'Material',
        quantity,
        unitPrice,
        unid: nextPaintingMaterial?.unid ?? null,
        total,
      }

      setValue(`${fieldName}.budgetResolutionMaterials`, nextMaterials, {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [getValues, setValue]
  )

  const getAndUpdateLaborTime = useCallback(
    (fieldName, service, squareMeter) => {
      const calculateBySquareMeter = service?.isExecutionTimePerSqm ?? false
      const ranges = service?.laborType?.laborTimeBySquareMeterRanges || []

      const nextLaborTime = calculateBySquareMeter
        ? getLaborTime(squareMeter, ranges)
        : service?.executionTimeHours || 0

      setValue(`${fieldName}.laborTime`, nextLaborTime, { shouldValidate: true })

      return nextLaborTime
    },
    [setValue]
  )

  const returnToPreviousValue = useCallback(
    fieldName => {
      const previousService = getValues(`${fieldName}.service`)
      Promise.resolve().then(() => {
        setValue(`${fieldName}.service`, previousService)
      })
    },
    [setValue, getValues]
  )

  const handleServiceDataUpdate = useCallback(
    (fieldName, value, compartmentIndex, resolutionIndex, updateDescription = true) => {
      const currentMaterials = getValues(`${fieldName}.budgetResolutionMaterials`)

      const squareMeter = getValues('squareFootage')

      const calculateBySquareMeter = value?.isExecutionTimePerSqm ?? false
      const validSquareMeter = Number.isFinite(Number(squareMeter)) && Number(squareMeter) !== 0

      if (calculateBySquareMeter) {
        if (!validSquareMeter) {
          showToast({
            type: 'error',
            message:
              'A metragem quadrada é necessária para calcular o tempo de mão de obra desse serviço',
          })

          returnToPreviousValue(fieldName)
          return false
        }

        const invalidService = validateSqmOnRange(squareMeter, [value])
        if (invalidService) {
          showToast({
            type: 'error',
            message: `Não há um range configurado para ${squareMeter} m² no Tipo de MO ${invalidService}. Entre em contato com o administrador para realizar o ajuste.`,
          })
          returnToPreviousValue(fieldName)
          return false
        }
      }

      setValue(`${fieldName}.service`, value, {
        shouldValidate: true,
      })

      // Update description only if required
      if (updateDescription) {
        setValue(`${fieldName}.serviceDescription`, value?.solutionDesc || '', {
          shouldValidate: true,
        })
      }

      const nextLaborTime = getAndUpdateLaborTime(fieldName, value, squareMeter)

      Promise.resolve().then(() => {
        getAndUpdateLaborValue({
          compartmentIndex,
          resolutionIndex,
          laborTime: nextLaborTime,
        })
      })

      Promise.resolve().then(() => updateExecutionDue())

      const isPaintingService = value?.laborType?.slug === LABOR_TYPE_SLUGS.PAINTING
      const serviceUnid = value?.unid || null

      const nextPaintingMaterial =
        isPaintingService && calculateBySquareMeter
          ? { ...getPaintingMaterial(squareMeter), unid: serviceUnid }
          : { unid: serviceUnid }

      if (!currentMaterials) {
        ensureDefaultMaterial(fieldName, value?.idealMaterial || 0, nextPaintingMaterial)
        return true
      }

      updateCurrentMaterial(
        currentMaterials[0],
        fieldName,
        value?.idealMaterial || 0,
        nextPaintingMaterial
      )

      return true
    },
    [
      getValues,
      setValue,
      showToast,
      getAndUpdateLaborTime,
      getAndUpdateLaborValue,
      updateExecutionDue,
      ensureDefaultMaterial,
    ]
  )

  return {
    handleServiceDataUpdate,
  }
}
